import { create } from 'zustand';
import * as api from '../lib/api';
import {
  createIdempotencyKey,
  normalizeServerInput,
  validateServerInput,
} from '../lib/createServer';
import type { ChannelMember, Guild } from '../lib/api';

export type ServerMember = ChannelMember;
export type ServerMemberLoadResult = { status: 'applied' | 'superseded' };

export interface CreateServerIntent {
  kind: 'intent';
  name: string;
  icon: string;
  idempotencyKey: string;
}

export interface CreateServerReceipt {
  kind: 'receipt';
  key: string;
  guildId: string;
  landingChannelId: string;
}

type CreateServerRecovery = CreateServerIntent | CreateServerReceipt;
export type GuildRefreshResult =
  | { status: 'applied'; guilds: Guild[] }
  | { status: 'superseded'; guilds: Guild[] };

interface ServerState {
  guilds: Guild[];
  guildsLoaded: boolean;
  members: ServerMember[];
  loaded: boolean;
  loadedGuildId: string | null;
  accountId: string | null;
  createRecovery: CreateServerRecovery | null;
  createTerminalGuildId: string | null;
  createBusy: boolean;
  createError: string;

  bindAccount: (accountId: string) => void;
  refreshGuilds: (forceFresh?: boolean) => Promise<GuildRefreshResult>;
  createServer: (name?: string, icon?: string) => Promise<CreateServerReceipt>;
  clearCreateReceipt: () => void;
  setCreateError: (message: string) => void;
  clearMembers: () => void;
  replaceMembers: (guildId: string, members: ServerMember[]) => void;
  replaceGuild: (guild: Guild) => void;
  loadMembers: (guildId: string) => Promise<ServerMemberLoadResult>;
  removeMember: (userId: string) => void;
  reset: () => void;
}

const RECOVERY_PREFIX = 'borgee:create-server:';
let membersGeneration = 0;
let accountGeneration = 0;
let guildsGeneration = 0;
let guildsInFlight: { generation: number; promise: Promise<GuildRefreshResult> } | null = null;

function recoveryKey(accountId: string): string {
  return RECOVERY_PREFIX + accountId;
}

// Guild endpoints retain their established Server wording. Translate only the
// error as it enters this top-level Project creation surface.
function projectifyCreateError(message: string): string {
  return message.replace(/\bServer(s)?\b/g, (_match, plural?: string) => (plural ? 'Projects' : 'Project'));
}

function loadRecovery(accountId: string): CreateServerRecovery | null {
  try {
    const raw = localStorage.getItem(recoveryKey(accountId));
    return raw ? (JSON.parse(raw) as CreateServerRecovery) : null;
  } catch {
    return null;
  }
}

function persistRecovery(accountId: string, recovery: CreateServerRecovery | null): void {
  try {
    if (recovery) localStorage.setItem(recoveryKey(accountId), JSON.stringify(recovery));
    else localStorage.removeItem(recoveryKey(accountId));
  } catch {
    throw new Error(
      recovery
        ? 'Could not save Project creation recovery. Check browser storage and try again.'
        : 'Could not clear Project creation recovery. Check browser storage and try again.',
    );
  }
}

export const useServerStore = create<ServerState>()((set, get) => ({
  guilds: [],
  guildsLoaded: false,
  members: [],
  loaded: false,
  loadedGuildId: null,
  accountId: null,
  createRecovery: null,
  createTerminalGuildId: null,
  createBusy: false,
  createError: '',

  bindAccount: (accountId) => {
    if (get().accountId === accountId) return;
    accountGeneration++;
    guildsGeneration++;
    guildsInFlight = null;
    set({
      accountId,
      guilds: [],
      guildsLoaded: false,
      createRecovery: loadRecovery(accountId),
      createTerminalGuildId: null,
      createBusy: false,
      createError: '',
    });
  },

  refreshGuilds: (forceFresh = false) => {
    const accountId = get().accountId;
    if (!accountId) return Promise.resolve({ status: 'superseded', guilds: [] });
    if (forceFresh) {
      // A refresh started before a successful create response cannot prove the
      // new membership exists. Supersede it and issue a post-commit read rather
      // than coalescing creation reconciliation onto a stale snapshot.
      guildsGeneration++;
      guildsInFlight = null;
    }
    const generation = guildsGeneration;
    if (guildsInFlight?.generation === generation) return guildsInFlight.promise;
    const promise = api
      .fetchGuilds()
      .then((guilds): GuildRefreshResult => {
        if (generation !== guildsGeneration || get().accountId !== accountId) {
          return { status: 'superseded', guilds };
        }
        const recovery = get().createRecovery;
        if (
          recovery?.kind === 'receipt' &&
          !guilds.some((guild) => guild.id === recovery.guildId)
        ) {
          // The membership response is authoritative even when browser storage
          // is unavailable. Never retain a revoked Server in live state merely
          // because its stale recovery key could not be removed.
          let storageWarning = '';
          try {
          persistRecovery(accountId, null);
          } catch {
            storageWarning = ' Browser storage could not be cleared; reload may retry recovery.';
          }
          set({
            guilds,
            guildsLoaded: true,
            createRecovery: null,
            createTerminalGuildId: recovery.guildId,
            createError: `You no longer have access to the created Project.${storageWarning}`,
          });
        } else {
          set({ guilds, guildsLoaded: true });
        }
        return { status: 'applied', guilds };
      })
      .finally(() => {
        if (guildsInFlight?.promise === promise) guildsInFlight = null;
      });
    guildsInFlight = { generation, promise };
    return promise;
  },

  createServer: async (name, icon) => {
    const accountId = get().accountId;
    if (!accountId) throw new Error('Sign in before creating a Project.');
    if (get().createBusy) throw new Error('Project creation is already in progress.');
    const generation = accountGeneration;
    const ownsResult = () => generation === accountGeneration && get().accountId === accountId;

    let recovery = get().createRecovery;
    if (!recovery) {
      const normalized = normalizeServerInput(name ?? '', icon ?? '');
      const errors = validateServerInput(normalized);
      if (errors.name || errors.icon) throw new Error(errors.name ?? errors.icon);
      recovery = {
        kind: 'intent',
        ...normalized,
        idempotencyKey: createIdempotencyKey(),
      };
      try {
        persistRecovery(accountId, recovery);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Could not save Project creation recovery.';
        set({ createError: message });
        throw error;
      }
      set({ createRecovery: recovery, createTerminalGuildId: null });
    }

    set({ createBusy: true, createError: '' });
    try {
      if (recovery.kind === 'intent') {
        const result = await api.createGuild(
          { name: recovery.name, icon: recovery.icon },
          recovery.idempotencyKey,
        );
        if (!ownsResult()) throw new Error('Project creation was superseded by an account change.');
        recovery = {
          kind: 'receipt',
          key: recovery.idempotencyKey,
          guildId: result.guild.id,
          landingChannelId: result.landing_channel_id,
        };
        persistRecovery(accountId, recovery);
        set({ createRecovery: recovery });
      }

      const receipt = recovery as CreateServerReceipt;
      const refreshed = await get().refreshGuilds(true);
      if (!ownsResult() || refreshed.status !== 'applied')
        throw new Error('The Project list refresh was superseded.');
      if (!refreshed.guilds.some((guild) => guild.id === receipt.guildId)) {
        // refreshGuilds already reconciles a missing receipt membership and
        // clears live recovery best-effort. Do not let a second storage removal
        // mask the authoritative access error.
        const terminalMessage =
          get().createError || 'You no longer have access to the created Project.';
        set({
          createRecovery: null,
          createTerminalGuildId: receipt.guildId,
          createError: terminalMessage,
        });
        throw new Error(terminalMessage);
      }
      return receipt;
    } catch (error) {
      if (!ownsResult()) throw error;
      const message = projectifyCreateError(error instanceof Error ? error.message : 'Could not create the Project.');
      const deterministicFailure =
        error instanceof api.ApiError && [400, 401, 403, 409].includes(error.status);
      if (deterministicFailure && recovery.kind === 'intent') {
        // A terminal server response unlocks editing in this session even if a
        // stale durable key cannot be removed. On reload the same idempotency
        // key can only replay the same terminal response; it must not keep the
        // modal permanently locked now.
        let storageWarning = '';
        try {
          persistRecovery(accountId, null);
        } catch {
          storageWarning = ' Browser storage could not be cleared; reload may retry this request.';
        }
        set({ createRecovery: null, createError: `${message}${storageWarning}` });
      } else {
        set({ createError: message });
      }
      throw error;
    } finally {
      if (ownsResult()) set({ createBusy: false });
    }
  },

  clearCreateReceipt: () => {
    const { accountId, createRecovery } = get();
    if (!accountId || createRecovery?.kind !== 'receipt') return;
    let createError = '';
    try {
      persistRecovery(accountId, null);
    } catch {
      // The Server context is already settled. Clear the in-memory receipt so
      // the UI cannot remain locked forever; a stale durable receipt is safe to
      // replay because the create command is idempotent.
      createError = 'Browser storage could not be cleared; reload may retry Project recovery.';
    }
    set({ createRecovery: null, createError });
  },

  setCreateError: (createError) => set({ createError }),

  clearMembers: () => {
    membersGeneration++;
    set({ members: [], loaded: false, loadedGuildId: null });
  },

  replaceMembers: (guildId, members) => {
    membersGeneration++;
    set({ members, loaded: true, loadedGuildId: guildId });
  },

  // Server profile changes are reflected immediately in every consumer of the
  // shared Server list: the selected-Server rail, header, and switcher.
  replaceGuild: (guild) =>
    set((state) => ({
      guilds: state.guilds.map((current) => current.id === guild.id ? guild : current),
    })),

  loadMembers: async (guildId) => {
    const generation = ++membersGeneration;
    set({ members: [], loaded: false, loadedGuildId: guildId });
    try {
      const members = await api.fetchGuildMembers(guildId);
      if (generation !== membersGeneration) return { status: 'superseded' };
      set({ members, loaded: true, loadedGuildId: guildId });
      return { status: 'applied' };
    } catch (error) {
      if (generation === membersGeneration) set({ loaded: false, loadedGuildId: guildId });
      throw error;
    }
  },

  removeMember: (userId) =>
    set((state) => ({
      members: state.members.filter((member) => member.user_id !== userId),
    })),

  reset: () => {
    const accountId = get().accountId;
    if (accountId) {
      try {
        persistRecovery(accountId, null);
      } catch {
        // Logout/reset must still clear in-memory account state when browser
        // storage is unavailable; the key is account-scoped and cannot leak to
        // a different account.
      }
    }
    membersGeneration++;
    accountGeneration++;
    guildsGeneration++;
    guildsInFlight = null;
    set({
      guilds: [],
      guildsLoaded: false,
      members: [],
      loaded: false,
      loadedGuildId: null,
      accountId: null,
      createRecovery: null,
      createTerminalGuildId: null,
      createBusy: false,
      createError: '',
    });
  },
}));
